import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import classnames from 'classnames';
import { GoogleMapsAutocompleteProps } from 'types/dynamicInputs';
import { useToast } from 'hooks/useToast';

/* eslint-disable no-undef */
declare global {
  interface Window {
    google: typeof google;
  }
}

const GoogleMapsAutocomplete: React.FC<GoogleMapsAutocompleteProps> = ({
  value,
  onChange,
  onBlur,
  label,
  error,
  pristine,
  disabled,
  onLocationSelect
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const notify = useToast();

  const inputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<Loader | null>(null);

  const getDisplayAddress = (inputValue?: GoogleMapsAutocompleteProps['value']) =>
    inputValue?.formattedAddress ||
    inputValue?.name ||
    [
      inputValue?.address?.lineOne,
      inputValue?.address?.city,
      inputValue?.address?.state,
      inputValue?.address?.postalCode
    ]
      .filter(Boolean)
      .join(', ');

  const [searchValue, setSearchValue] = useState(getDisplayAddress(value) || '');

  const validateAddressComponents = (
    streetNumber: string,
    route: string,
    city: string,
    state: string,
    postalCode: string,
    formattedAddress?: string
  ): boolean => {
    const missingComponents = [];

    if (!streetNumber && !route && !formattedAddress) {
      missingComponents.push('street address');
    }
    if (!city) {
      missingComponents.push('city');
    }
    if (!state) {
      missingComponents.push('state');
    }
    if (!postalCode) {
      missingComponents.push('postal code');
    }

    if (missingComponents.length > 0) {
      const missingText = missingComponents.join(', ');
      notify(`Incomplete address data. Missing: ${missingText}. Please try selecting a different address.`);
      return false;
    }

    return true;
  };

  useEffect(() => {
    const initGoogleMaps = async () => {
      try {
        loaderRef.current = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
          version: 'weekly',
          libraries: ['places']
        });

        await loaderRef.current.load();
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading Google Maps:', error);
        setIsLoading(false);
      }
    };

    initGoogleMaps();
  }, []);

  useEffect(() => {
    if (!isLoading && inputRef.current && !autocomplete && map) {
      const autocompleteInstance = new google.maps.places.Autocomplete(inputRef.current, {
        types: ['establishment', 'geocode'],
        fields: ['name', 'formatted_address', 'geometry', 'address_components']
      });

      autocompleteInstance.addListener('place_changed', () => {
        const place = autocompleteInstance.getPlace();

        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          // Parse address components
          const addressComponents = place.address_components || [];
          let streetNumber = '';
          let route = '';
          let city = '';
          let state = '';
          let postalCode = '';

          addressComponents.forEach(component => {
            const types = component.types;

            if (types.includes('street_number')) {
              streetNumber = component.long_name;
            } else if (types.includes('route')) {
              route = component.long_name;
            } else if (types.includes('locality')) {
              city = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            } else if (types.includes('postal_code')) {
              postalCode = component.long_name;
            }
          });

          // Validate that all required address components are present
          if (!validateAddressComponents(streetNumber, route, city, state, postalCode, place.formatted_address)) {
            return; // Don't update the form if validation fails
          }

          const addressLineOne =
            streetNumber && route ? `${streetNumber} ${route}` : route || place.formatted_address || '';
          const formattedAddress =
            place.formatted_address ||
            (place as any).formattedAddress ||
            [addressLineOne, city, state, postalCode].filter(Boolean).join(', ');

          const newValue = {
            name: formattedAddress,
            formattedAddress,
            address: {
              lineOne: addressLineOne,
              lineTwo: '',
              city,
              state,
              postalCode
            },
            coordinates: {
              lat,
              lng
            }
          };

          onChange(newValue);
          setSearchValue(formattedAddress);

          // Call callback to update form fields
          if (onLocationSelect) {
            onLocationSelect({
              name: formattedAddress,
              formattedAddress,
              lineOne: addressLineOne,
              lineTwo: '',
              city,
              state,
              postalCode,
              coordinates: { lat, lng }
            });
          }

          // Update map to show the selected location
          map.setCenter({ lat, lng });
          map.setZoom(16);

          // Remove existing markers
          markersRef.current.forEach(m => m.setMap(null));
          const newMarker = new google.maps.Marker({
            position: { lat, lng },
            map,
            draggable: false
          });
          markersRef.current = [newMarker];
          setMarkers([newMarker]);
        }
      });

      setAutocomplete(autocompleteInstance);
    }
  }, [isLoading, autocomplete, map, markers, searchValue, value, onChange, onLocationSelect]);

  useEffect(() => {
    const nextValue = getDisplayAddress(value) || '';
    if (nextValue && nextValue !== searchValue) {
      setSearchValue(nextValue);
    }
  }, [value]);

  useEffect(() => {
    if (!isLoading && mapRef.current && !map) {
      const defaultCenter = value?.coordinates
        ? { lat: value.coordinates.lat, lng: value.coordinates.lng }
        : { lat: 35.7796, lng: -78.6382 }; // Default to North Carolina

      const mapInstance = new google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: 16,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        draggable: false,
        zoomControl: true,
        scrollwheel: false,
        disableDoubleClickZoom: true,
        gestureHandling: 'none',
        panControl: false,
        rotateControl: false,
        scaleControl: false,
        cameraControl: false
      });

      setMap(mapInstance);

      // If we have initial coordinates, create a marker immediately
      if (value?.coordinates) {
        const initialMarker = new google.maps.Marker({
          position: { lat: value.coordinates.lat, lng: value.coordinates.lng },
          map: mapInstance,
          draggable: false
        });
        markersRef.current = [initialMarker];
        setMarkers([initialMarker]);
      }
    }
  }, [isLoading, map, markers, value, onChange, onLocationSelect]);

  useEffect(() => {
    if (value?.coordinates && map) {
      const position = { lat: value.coordinates.lat, lng: value.coordinates.lng };

      if (markers.length > 0) {
        // Update existing marker
        map.setCenter(position);
        markers[0].setPosition(position);
      } else {
        // Create new marker if none exists
        map.setCenter(position);
        const newMarker = new google.maps.Marker({
          position,
          map,
          draggable: false
        });
        markersRef.current = [newMarker];
        setMarkers([newMarker]);
      }
    }
  }, [value?.coordinates, map, markers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleInputBlur = () => {
    onBlur && onBlur();
  };

  if (isLoading) {
    return (
      <div className="input-wrapper">
        {!!label && <label className="label">{label}</label>}
        <div className="google-maps-loading">Loading Google Maps...</div>
      </div>
    );
  }

  return (
    <div className="google-maps-autocomplete">
      <div className="input-wrapper">
        {!!label && <label className="label">{label}</label>}
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          placeholder="Search for a location..."
          className={classnames('google-maps-input', { error: !pristine && error })}
        />
        {!pristine && !!error && <span className="error-message body-2">{error}</span>}
      </div>

      <div className="map-container">
        <div ref={mapRef} className="google-map" />
      </div>
    </div>
  );
};

export default GoogleMapsAutocomplete;
